import * as React from "react";
import { Stack } from "@mui/material";
import { useState } from "react";
import AssemblyDetails from "./AssemblyDetails";
import {
  DocumentReference,
  insertSDT,
  updateExistingSDT,
  deleteExistingSDT,
} from "../../../utils/taskpane";
import {
  useAddReferenceMutation,
  useUpdateReferenceMutation,
  useDeleteReferenceMutation,
  useAddConditionMutation,
  useDeleteConditionMutation,
  useAddTemplateColumnMutation,
  useDeleteTemplateColumnMutation,
  useGetAssemblyQuery,
} from "../api/playbookApi";
import { AssemblyApiContext } from "../types/assembly-context";
import { AssemblyProvider } from "../context/AssemblyContext";
import { AssemblyReferenceModel, AssemblyReferenceWriteModel } from "../types/assembly-reference";
import EditReferenceView from "./EditReferenceView";
import AddReferenceView from "./AddReferenceView";
import ReferenceList from "./ReferenceList";
import ExistingReferences from "./ExistingReferences";

interface PlaybookAssemblyWrapperProps {
  ctx: AssemblyApiContext;
}

const PlaybookAssemblyWrapper: React.FC<PlaybookAssemblyWrapperProps> = ({ ctx }) => {
  const [currentView, setCurrentView] = useState<"list" | "add" | "edit" | "existing">("list");
  const [selectedReference, setSelectedReference] = useState<DocumentReference | null>(null);
  const [previousView, setPreviousView] = useState<"list" | "existing">("list");

  const { data: assembly } = useGetAssemblyQuery(ctx);

  const [addReference, { isLoading: isAdding, error: addError }] = useAddReferenceMutation();
  const [updateReference, { isLoading: isUpdating, error: updateError }] =
    useUpdateReferenceMutation();
  const [deleteReference, { isLoading: isDeleting, error: deleteError }] =
    useDeleteReferenceMutation();
  const [addCondition] = useAddConditionMutation();
  const [deleteCondition] = useDeleteConditionMutation();
  const [addTemplateColumn] = useAddTemplateColumnMutation();
  const [deleteTemplateColumn] = useDeleteTemplateColumnMutation();

  const handleBackToList = () => {
    setCurrentView("list");
    setSelectedReference(null);
  };

  const handleBackToPrevious = () => {
    setCurrentView(previousView);
    setSelectedReference(null);
  };

  const handleEditReference = (reference: DocumentReference) => {
    setPreviousView(currentView === "existing" ? "existing" : "list");
    setSelectedReference(reference);
    setCurrentView("edit");
  };

  const handleAddReference = async (model: AssemblyReferenceWriteModel, promptLabel: string) => {
    try {
      const res = await addReference({ ctx, model }).unwrap();
      await insertSDT(promptLabel, res.stableObjectIdentifier);
      setCurrentView("list");
    } catch (err) {
      console.error("Failed to add reference:", err);
    }
  };

  // Updating: the reference PUT only carries formats + template. Conditions and
  // template columns are managed through their own endpoints, so they are
  // re-synced here by removing the previous set and re-creating from the form.
  const handleUpdateReference = async (
    model: AssemblyReferenceWriteModel,
    promptLabel: string,
    existing: AssemblyReferenceModel
  ) => {
    if (!selectedReference) return;
    try {
      const referenceId = existing.objectIdentifier;
      await updateReference({ ctx, referenceId, model }).unwrap();

      await Promise.all(
        existing.conditions.map((condition) =>
          deleteCondition({ ctx, referenceId, conditionId: condition.objectIdentifier }).unwrap()
        )
      );
      await Promise.all(
        (model.conditions ?? []).map((condition) =>
          addCondition({ ctx, referenceId, condition }).unwrap()
        )
      );

      await Promise.all(
        existing.templateColumns.map((column) =>
          deleteTemplateColumn({ ctx, referenceId, columnId: column.objectIdentifier }).unwrap()
        )
      );
      await Promise.all(
        (model.templateColumns ?? []).map((column) =>
          addTemplateColumn({ ctx, referenceId, column }).unwrap()
        )
      );

      await updateExistingSDT(selectedReference.stableObjectIdentifier, promptLabel);
      handleBackToPrevious();
    } catch (err) {
      console.error("Failed to update reference:", err);
    }
  };

  const handleDeleteReference = async (existing: AssemblyReferenceModel) => {
    if (!selectedReference) return;
    try {
      await deleteReference({ ctx, referenceId: existing.objectIdentifier }).unwrap();
      await deleteExistingSDT(selectedReference.stableObjectIdentifier);
      handleBackToPrevious();
    } catch (err) {
      console.error("Failed to delete reference:", err);
    }
  };

  return (
    <AssemblyProvider value={ctx}>
      <Stack height="100%" overflow="hidden">
        <AssemblyDetails />
        <Stack flex={1} sx={{ overflowY: "auto" }} direction="column">
          {currentView === "list" && (
            <ReferenceList
              onAddReference={() => setCurrentView("add")}
              onEditReference={handleEditReference}
              onViewExisting={() => setCurrentView("existing")}
            />
          )}
          {currentView === "existing" && (
            <ExistingReferences onEditReference={handleEditReference} onBack={handleBackToList} />
          )}
          {currentView === "add" && assembly && (
            <AddReferenceView
              assemblyType={assembly.assemblyType}
              assignedGroupObjectIdentifier={assembly.assignedGroupObjectIdentifier}
              onBack={handleBackToList}
              onSubmit={handleAddReference}
              loading={isAdding}
              error={!!addError}
            />
          )}
          {currentView === "edit" && selectedReference && assembly && (
            <EditReferenceView
              assemblyType={assembly.assemblyType}
              assignedGroupObjectIdentifier={assembly.assignedGroupObjectIdentifier}
              selectedReference={selectedReference}
              onBack={handleBackToPrevious}
              onUpdateReference={handleUpdateReference}
              onDeleteReference={handleDeleteReference}
              loading={isUpdating || isDeleting}
              error={!!updateError || !!deleteError}
            />
          )}
        </Stack>
      </Stack>
    </AssemblyProvider>
  );
};

export default PlaybookAssemblyWrapper;
